import { getNodeLayoutDimensions, type NodeCenterPct } from "./spiderweb-utils";

import type { AnimationPhase, WebNodeConfig } from "./spiderweb-types";

function easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function clamp01(v: number): number {
    return Math.min(1, Math.max(0, v));
}

function clampMinSize(v: number): number {
    return Math.max(1, v);
}

function dimsForSize(node: WebNodeConfig, sz: number) {
    return getNodeLayoutDimensions({ ...node, size: sz });
}

export function assertConsistentScrollWindows(
    nodeId: string,
    phases: AnimationPhase[] | undefined
): void {
    if (!phases?.length) {
        return;
    }
    const anyMarked = phases.some(
        (p) =>
            p.scrollWindowStart !== undefined || p.scrollWindowEnd !== undefined
    );
    if (!anyMarked) {
        return;
    }
    if (!animationPhasesUseScrollWindows(phases)) {
        throw new Error(
            `SpiderWeb node "${nodeId}": set scrollWindowStart and scrollWindowEnd on every phase, or on none.`
        );
    }
}

export function animationPhasesUseScrollWindows(
    phases: AnimationPhase[] | undefined
): boolean {
    if (!phases?.length) {
        return false;
    }
    return phases.every(
        (p) =>
            p.scrollWindowStart !== undefined && p.scrollWindowEnd !== undefined
    );
}

export type ScrollWindowPhaseTarget = Partial<
    Pick<AnimationPhase, "x" | "y" | "opacity" | "size" | "width" | "height">
>;

export function scrollWindowPhase(
    scrollWindowStart: number,
    scrollWindowEnd: number,
    target: ScrollWindowPhaseTarget
): AnimationPhase {
    return {
        delay: 0,
        duration: 0,
        scrollWindowStart,
        scrollWindowEnd,
        ...(target.x !== undefined ? { x: target.x } : {}),
        ...(target.y !== undefined ? { y: target.y } : {}),
        ...(target.opacity !== undefined ? { opacity: target.opacity } : {}),
        ...(target.size !== undefined ? { size: target.size } : {}),
        ...(target.width !== undefined ? { width: target.width } : {}),
        ...(target.height !== undefined ? { height: target.height } : {})
    };
}

// eslint-disable-next-line complexity
export function getLayoutAtScrollWindows(
    node: WebNodeConfig,
    baseX: number,
    baseY: number,
    baseOpacity: number,
    phases: AnimationPhase[],
    scrollPercent100: number
): NodeCenterPct {
    const o0 = clamp01(baseOpacity);
    const bases = getNodeLayoutDimensions(node);
    let x = baseX;
    let y = baseY;
    let opacity = o0;
    let sz = bases.size;
    let bw = bases.boxWidth;
    let bh = bases.boxHeight;

    const sorted = [...phases].sort(
        (a, b) => (a.scrollWindowStart ?? 0) - (b.scrollWindowStart ?? 0)
    );

    for (const p of sorted) {
        const ws = p.scrollWindowStart ?? 0;
        const we = p.scrollWindowEnd ?? 0;
        if (we <= ws) {
            continue;
        }

        if (scrollPercent100 < ws) {
            return { x, y, opacity, size: sz, width: bw, height: bh };
        }

        const opacityStart = opacity;
        const opacityEnd =
            p.opacity !== undefined ? clamp01(p.opacity) : opacity;
        const szStart = sz;
        const bwStart = bw;
        const bhStart = bh;
        const szEnd = p.size !== undefined ? clampMinSize(p.size) : szStart;
        const bwEnd =
            p.width !== undefined
                ? p.width
                : p.size !== undefined
                ? dimsForSize(node, szEnd).boxWidth
                : bwStart;
        const bhEnd =
            p.height !== undefined
                ? p.height
                : p.size !== undefined
                ? dimsForSize(node, szEnd).boxHeight
                : bhStart;

        if (scrollPercent100 < we) {
            const span = we - ws;
            const u = span > 0 ? (scrollPercent100 - ws) / span : 1;
            const e = easeInOutCubic(Math.min(1, Math.max(0, u)));
            const szMid = szStart + (szEnd - szStart) * e;
            const opacityMid = opacityStart + (opacityEnd - opacityStart) * e;
            const xEnd = p.x !== undefined ? p.x : x;
            const yEnd = p.y !== undefined ? p.y : y;

            let bwMid: number;
            if (p.width !== undefined) {
                bwMid = bwStart + (bwEnd - bwStart) * e;
            } else if (p.size !== undefined) {
                bwMid = dimsForSize(node, szMid).boxWidth;
            } else {
                bwMid = bwStart;
            }

            let bhMid: number;
            if (p.height !== undefined) {
                bhMid = bhStart + (bhEnd - bhStart) * e;
            } else if (p.size !== undefined) {
                bhMid = dimsForSize(node, szMid).boxHeight;
            } else {
                bhMid = bhStart;
            }

            return {
                x: x + (xEnd - x) * e,
                y: y + (yEnd - y) * e,
                opacity: opacityMid,
                size: szMid,
                width: bwMid,
                height: bhMid
            };
        }

        x = p.x !== undefined ? p.x : x;
        y = p.y !== undefined ? p.y : y;
        opacity = opacityEnd;
        sz = szEnd;
        bw = bwEnd;
        bh = bhEnd;
    }

    return { x, y, opacity, size: sz, width: bw, height: bh };
}
